import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ClickAwayListener,
  Fade,
  InputAdornment,
  ListItem,
  TextField,
} from "@mui/material";
import CaretDownIcon from "assets/icons/CaretDownIcon";
import HtmlTooltip from "components/UI/HtmlTooltip/HtmlTooltip";
import LoadingIndicator from "components/UI/LoadingIndicator/LoadingIndicator";
import { useGetAllAdvertisers } from "graphql/advertiser/hooks";
import { AdvertiserListItem } from "graphql/advertiser/types";
import useShowToast from "hooks/useShowToast";
import useViewByRole from "hooks/useViewByRole";
import deburr from "lodash/deburr";
import { useEffect, useRef, useState } from "react";
import { usePageStore } from "store/page";
import { useUserStore } from "store/user";
import { colors } from "theme";
import { NAV_MENU_WIDTH, USER_ROLES } from "utils/constants";
import { getStandardTextFieldSx } from "utils/muiFieldStyles";
import { styles } from "./AdvertiserDropdownStyles";

const AdvertiserDropdown = () => {
  const [open, setOpen] = useState(false);
  const navMenuOpen = usePageStore((state) => state.navMenuOpen);

  const [filter, setFilter] = useState<string>("");
  const [advertisers, setAdvertisers] = useState<AdvertiserListItem[]>([]);

  const userHasRole = useViewByRole();

  const { showError } = useShowToast();

  const { data: availableAdvertisers, error, loading } = useGetAllAdvertisers();

  const { selectedAdvertiser, setSelectedAdvertiser } = useUserStore(
    (state) => ({
      selectedAdvertiser: state.selectedAdvertiser,
      setSelectedAdvertiser: state.setSelectedAdvertiser,
    }),
  );

  const filterRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (error) showError(error.message);
  }, [error]);

  useEffect(() => {
    if (open) {
      filterRef.current?.focus();
    }
  }, [open]);

  function onFilterChange(event) {
    const value = event.target.value;
    setFilter(value);
  }

  useEffect(() => {
    function filterAdvertisers() {
      const normalize = (text: string) => deburr(text.trim().toLowerCase());
      const filterByName = ({ name }) =>
        normalize(name).includes(normalize(filter));
      const filteredAdvertisers =
        availableAdvertisers?.advertisers?.filter(filterByName);

      if (userHasRole(USER_ROLES.ADMIN)) {
        // Only sort for admins
        // For the other roles backend already returns a sorted list
        return filteredAdvertisers.sort((a, b) => a.name.localeCompare(b.name));
      }
      return filteredAdvertisers;
    }

    if (availableAdvertisers?.advertisers?.length > 0) {
      setAdvertisers(filterAdvertisers());
    }
  }, [availableAdvertisers, filter]);

  const backdropLeft = navMenuOpen
    ? NAV_MENU_WIDTH.open
    : NAV_MENU_WIDTH.closed;

  return (
    <>
      <HtmlTooltip
        title="Switch Advertiser"
        placement="bottom"
        fontSize={12}
        noShadow
        inverse
      >
        <Box
          sx={styles.root}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((open) => !open);
          }}
        >
          <CaretDownIcon
            data-cy="advertiser-dropdown"
            height={7}
            color={selectedAdvertiser?.id ? colors.softBlack : colors.red[500]}
            style={{
              transform: open ? "rotate(180deg)" : null,
            }}
          />
        </Box>
      </HtmlTooltip>

      <Fade in={open} timeout={{ enter: 300, exit: 250 }}>
        <Box
          sx={{
            ...styles.backdrop,
            width: `calc(100% - ${backdropLeft}px)`,
            left: backdropLeft,
          }}
        >
          <ClickAwayListener
            mouseEvent={open ? "onClick" : false}
            touchEvent={open ? "onTouchEnd" : false}
            onClickAway={() => open && setOpen(false)}
          >
            <Box sx={styles.selectArea}>
              <ListItem sx={styles.searchInput} dense>
                <TextField
                  inputRef={filterRef}
                  fullWidth
                  variant="standard"
                  sx={{
                    ...getStandardTextFieldSx(),
                    "& .MuiInput-input": {
                      paddingBottom: 0.5,
                      paddingTop: 0.5,
                      fontSize: 14,
                      height: 24,
                      boxSizing: "border-box",
                    },
                  }}
                  onChange={onFilterChange}
                  placeholder="Search by Advertiser Name"
                  value={filter}
                  inputProps={{
                    "data-cy": "advertiser-input",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: colors.softBlack }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </ListItem>
              {loading && advertisers?.length === 0 ? (
                <LoadingIndicator size={40}></LoadingIndicator>
              ) : (
                advertisers.map((advertiser) => (
                  <ListItem
                    data-cy={`advertiser-${advertiser.id}`}
                    key={advertiser.id}
                    dense
                    onClick={() => {
                      setSelectedAdvertiser(advertiser);
                      setOpen(false);
                    }}
                    sx={styles.listItemRoot}
                    style={{ cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div style={{ fontSize: 14 }}>
                        {advertiser.name}
                        {advertiser.cmsId ? ` (${advertiser.cmsId})` : null}
                      </div>
                    </div>
                  </ListItem>
                ))
              )}
            </Box>
          </ClickAwayListener>
        </Box>
      </Fade>
    </>
  );
};

export default AdvertiserDropdown;
